import { createOptions, Options } from 'options';
import type { ActivityType, ProductKind } from 'types';

export const applicationStates = ['EMPTY', 'NONE', 'PARTIAL', 'FULL', 'OVERSPENT'] as const;
export type ApplicationState = (typeof applicationStates)[number];

export const applicationStateOptions = createOptions(applicationStates);

export const factorKinds = ['CONSTANT', 'WEEKLY', 'MONTHLY', 'LENGTH'] as const;
export type FactorKind = (typeof factorKinds)[number];
export const factorKindOptions = createOptions(factorKinds);

export const factorForms = ['PERCENTAGE', 'COEFFICIENT'] as const;
export type FactorForm = (typeof factorForms)[number];
export const factorFormOptions = createOptions(factorForms);

export const pricingModels = ['DISCOUNT', 'FIXED', 'RATE'] as const;
export type PricingModel = (typeof pricingModels)[number];

export const pricingModelLabels = {
  DISCOUNT: 'Discount',
  FIXED: 'Fixed net',
  RATE: 'Custom gross',
} as const;
export const pricingModelOptions = createOptions(pricingModels, pricingModelLabels);

// process stages for stauses, states
export const stages = ['PENDING', 'ACTIVE', 'DONE', 'CANCELED'] as const;
export type Stage = (typeof stages)[number];

const baseUnits = ['UNIT', 'HOUR', 'PERIOD'] as const;
const temporalUnits = ['DAY', 'WEEK', 'MONTH'] as const;
const RTBUnits = ['CPM', 'CPC', 'PER_CONTACT', 'CPV', 'PUBLICATION', 'VCPM', 'CPL', 'CPA', 'CPI', 'CPS'] as const;
const radioUnits = ['SPOT'] as const;

export type Unit =
  | (typeof baseUnits)[number]
  | (typeof temporalUnits)[number]
  | (typeof RTBUnits)[number]
  | (typeof radioUnits)[number];

// an insertion is one appearance of one position in one issue
const pressUnits = ['PUBLICATION'] as const;

const genericUnitOptions = createOptions<Unit>([...baseUnits, ...temporalUnits], undefined, (v) => v);
const rtbUnitOptions = createOptions<Unit>([...baseUnits, ...temporalUnits, ...RTBUnits], undefined, (v) => v);
const pressUnitOptions = createOptions<Unit>([...baseUnits, ...temporalUnits, ...pressUnits], undefined, (v) => v);

export const allUnitOptions = createOptions<Unit>([...baseUnits, ...temporalUnits, ...RTBUnits], undefined, (v) => v);

type TemporalUnit = (typeof temporalUnits)[number];
export function isTemporal(unit: Unit): unit is TemporalUnit {
  return temporalUnits.includes(unit as TemporalUnit);
}

// Important to keep in sync with backend for validation
export function getUnitOptions(kind: ProductKind, type: ActivityType): Options<Unit> {
  if (kind === 'MEDIA') {
    switch (type) {
      case 'RTB':
      case 'WEB':
        return rtbUnitOptions;
      case 'PRESS':
        return pressUnitOptions;
    }
  }

  return genericUnitOptions;
}

export type Interval = {
  start: Date;
  end: Date;
};
